import { useState, useMemo, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  Filter,
  Settings2,
  Download,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Monitor,
  TestTube2,
  Database,
  Trash2,
  Edit,
  ArrowRightLeft,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/filter-utils";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
  prepareDataForExport,
  getVisibleColumns,
  getAllColumns,
} from "@/lib/export-utils";
import { StockMovementDetailsDrawer } from "@/components/stock-movement-details-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useScreenMode } from "@/providers/ModeProvider";
import { useStockMovements, StockMovementSortInput, StockMovementFilterInput } from "@/hooks/use-stock-movements";
import { getStockMovements } from "@/services/stock-movement-service";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SortOrder } from "@/hooks/use-graphql-fetch";

interface StockMovement {
  id: string;
  reference: string;
  date: Date;
  type:
    | "Sales"
    | "Purchase"
    | "Opening Balance"
    | "Write-off"
    | "Transfer"
    | "Manual Adjustment"
    | "Stock Take Adjustment";
  source: "Manual" | "ERP Sync" | "System Generated";
  stockCode: string;
  stockDescription: string;
  location: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  userName: string;
  notes: string | null;
}

type SortColumn = "date" | "reference" | "type" | "source" | "stockCode" | "quantity" | "unitPrice" | "lineTotal";
type SortDirection = "asc" | "desc";

// All available columns with descriptions
const ALL_COLUMNS: ColumnDefinition[] = [
  { id: "date", name: "Date", description: "Movement date" },
  { id: "reference", name: "Reference", description: "Transaction reference number" },
  { id: "type", name: "Type", description: "Movement type" },
  { id: "source", name: "Source", description: "Origin of movement" },
  { id: "stockCode", name: "Stock Code", description: "Stock item code" },
  { id: "description", name: "Description", description: "Stock item description" },
  { id: "location", name: "Location", description: "Warehouse or location" },
  { id: "quantity", name: "Quantity", description: "Quantity moved (+ or -)" },
  { id: "unitPrice", name: "Unit Price", description: "Price per unit" },
  { id: "totalValue", name: "Total Value", description: "Line total value" },
  { id: "user", name: "User", description: "User who created movement" },
  { id: "notes", name: "Notes", description: "Additional notes" },
];

// Default visible columns (9 columns)
const DEFAULT_VISIBLE_COLUMNS = [
  "date",
  "reference",
  "type",
  "source",
  "stockCode",
  "description",
  "quantity",
  "totalValue",
  "user",
];

// Generate 120 realistic mock stock movements
const generateMockMovements = (): StockMovement[] => {
  const movements: StockMovement[] = [];
  const today = new Date();

  const movementTypes: StockMovement["type"][] = [
    "Sales",
    "Sales",
    "Sales", // 30%
    "Purchase",
    "Purchase",
    "Purchase", // 25%
    "Manual Adjustment",
    "Manual Adjustment", // 20%
    "Transfer", // 10%
    "Write-off", // 8%
    "Opening Balance", // 5%
    "Stock Take Adjustment", // 2%
  ];

  const sources: StockMovement["source"][] = ["Manual", "ERP Sync", "System Generated"];

  const stockCodes = [
    "STK001",
    "STK002",
    "STK003",
    "STK004",
    "STK005",
    "STK006",
    "STK007",
    "STK008",
    "STK009",
    "STK010",
    "STK011",
    "STK012",
    "STK013",
    "STK014",
    "STK015",
    "STK016",
    "STK017",
    "STK018",
    "STK019",
    "STK020",
  ];

  const stockDescriptions = [
    "Laptop Dell XPS 15",
    "Office Chair Ergonomic",
    "A4 Paper Ream (500 sheets)",
    "Steel Rods 10mm x 6m",
    "Wireless Mouse Logitech",
    "Standing Desk Adjustable",
    "Ballpoint Pens Blue (Box of 50)",
    "Aluminum Sheets 2mm",
    'Monitor 27" 4K Dell',
    "Filing Cabinet 4-Drawer",
    "Stapler Heavy Duty",
    "Copper Wire 2.5mm",
    "Keyboard Mechanical RGB",
    "Conference Table 8-Seater",
    "Sticky Notes 3x3 (Pack of 12)",
    "PVC Pipes 50mm x 3m",
    "Webcam HD 1080p",
    "Bookshelf 5-Tier",
    "Printer Paper A3 (250 sheets)",
    "Timber Planks Oak 2x4",
  ];

  const locations = ["Warehouse A", "Warehouse B", "Warehouse C", "Distribution Center", "Retail Store", null];

  const userNames = [
    "John Smith",
    "Sarah Johnson",
    "Michael Brown",
    "Emma Wilson",
    "David Lee",
    "System",
    "ERP Integration",
  ];

  const notesSamples = [
    "Routine stock replenishment",
    "Customer order fulfillment",
    "Damaged goods write-off",
    "Annual stock take adjustment",
    "Transfer between warehouses",
    "Opening balance entry",
    "Supplier delivery received",
    null,
    null,
    null,
  ];

  // Generate 120 movements
  for (let i = 0; i < 120; i++) {
    const daysAgo = Math.floor(Math.random() * 180); // Random date within last 6 months
    const movementDate = new Date(today);
    movementDate.setDate(movementDate.getDate() - daysAgo);

    const type = movementTypes[Math.floor(Math.random() * movementTypes.length)];

    // Determine source based on type
    let source: StockMovement["source"];
    if (type === "Stock Take Adjustment") {
      source = Math.random() < 0.7 ? "System Generated" : "Manual";
    } else if (type === "Sales" || type === "Purchase") {
      source = Math.random() < 0.6 ? "ERP Sync" : "Manual";
    } else {
      source = Math.random() < 0.7 ? "Manual" : "ERP Sync";
    }

    // Generate reference based on type
    let reference: string;
    const refNum = String(i + 1).padStart(3, "0");
    switch (type) {
      case "Sales":
        reference = `SO-2024-${refNum}`;
        break;
      case "Purchase":
        reference = `PO-2024-${refNum}`;
        break;
      case "Opening Balance":
        reference = `OB-${refNum}`;
        break;
      case "Write-off":
        reference = `WO-${refNum}`;
        break;
      case "Transfer":
        reference = `TRF-${refNum}`;
        break;
      case "Manual Adjustment":
        reference = `ADJ-${refNum}`;
        break;
      case "Stock Take Adjustment":
        reference = `STA-${refNum}`;
        break;
      default:
        reference = `MOV-${refNum}`;
    }

    const stockIndex = Math.floor(Math.random() * stockCodes.length);
    const stockCode = stockCodes[stockIndex];
    const stockDescription = stockDescriptions[stockIndex];

    const location = locations[Math.floor(Math.random() * locations.length)];

    // Generate quantity (positive for increases, negative for decreases)
    let quantity: number;
    if (type === "Sales" || type === "Write-off") {
      quantity = -(Math.floor(Math.random() * 50) + 1); // -1 to -50
    } else if (type === "Purchase" || type === "Opening Balance") {
      quantity = Math.floor(Math.random() * 100) + 10; // 10 to 110
    } else {
      // Adjustments and transfers can be positive or negative
      quantity = Math.random() < 0.5 ? Math.floor(Math.random() * 50) + 1 : -(Math.floor(Math.random() * 50) + 1);
    }

    const unitPrice = parseFloat((Math.random() * 500 + 5).toFixed(2)); // £5 to £505
    const lineTotal = parseFloat((quantity * unitPrice).toFixed(2));

    // Determine user based on source
    let userName: string;
    if (source === "System Generated") {
      userName = "System";
    } else if (source === "ERP Sync") {
      userName = "ERP Integration";
    } else {
      userName = userNames[Math.floor(Math.random() * (userNames.length - 2))]; // Exclude System and ERP Integration
    }

    const notes = notesSamples[Math.floor(Math.random() * notesSamples.length)];

    movements.push({
      id: `MOV${String(i + 1).padStart(3, "0")}`,
      reference,
      date: movementDate,
      type,
      source,
      stockCode,
      stockDescription,
      location,
      quantity,
      unitPrice,
      lineTotal,
      userName,
      notes,
    });
  }

  // Add specific transaction history reference movements
  const specificMovements: StockMovement[] = [
    // Stock Adjustments
    {
      id: "MOV-ADJ-015",
      reference: "ADJ-2024-015",
      date: new Date("2024-01-20"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A",
      quantity: -5,
      unitPrice: 25.0,
      lineTotal: -125.0,
      userName: "John Smith",
      notes: "Damaged units - water damage during storage",
    },
    {
      id: "MOV-ADJ-012",
      reference: "ADJ-2024-012",
      date: new Date("2024-02-10"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A",
      quantity: -3,
      unitPrice: 25.0,
      lineTotal: -75.0,
      userName: "Sarah Johnson",
      notes: "Theft - security incident reported",
    },
    {
      id: "MOV-ADJ-018",
      reference: "ADJ-2024-018",
      date: new Date("2024-04-05"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK003",
      stockDescription: "Ergonomic Office Chair",
      location: "Warehouse B",
      quantity: -2,
      unitPrice: 150.0,
      lineTotal: -300.0,
      userName: "Michael Brown",
      notes: "Damaged during handling",
    },
    {
      id: "MOV-ADJ-005",
      reference: "ADJ-2024-005",
      date: new Date("2024-05-10"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK003",
      stockDescription: "Ergonomic Office Chair",
      location: "Warehouse B",
      quantity: 5,
      unitPrice: 150.0,
      lineTotal: 750.0,
      userName: "Emma Wilson",
      notes: "Stocktake correction - found additional units",
    },
    // Stock Transfers
    {
      id: "MOV-TRF-011",
      reference: "TRF-2024-011",
      date: new Date("2024-01-28"),
      type: "Transfer",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A → Warehouse B",
      quantity: 50,
      unitPrice: 25.0,
      lineTotal: 1250.0,
      userName: "David Lee",
      notes: "Transfer to Warehouse B for regional distribution",
    },
    {
      id: "MOV-TRF-007",
      reference: "TRF-2024-007",
      date: new Date("2024-02-18"),
      type: "Transfer",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse B → Distribution Center",
      quantity: 30,
      unitPrice: 25.0,
      lineTotal: 750.0,
      userName: "John Smith",
      notes: "Transfer to distribution center for customer orders",
    },
    {
      id: "MOV-TRF-002",
      reference: "TRF-2024-002",
      date: new Date("2024-03-15"),
      type: "Transfer",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A → Retail Store",
      quantity: 20,
      unitPrice: 25.0,
      lineTotal: 500.0,
      userName: "Sarah Johnson",
      notes: "Transfer to retail store for display and sales",
    },
    {
      id: "MOV-TRF-012",
      reference: "TRF-2024-012",
      date: new Date("2024-04-20"),
      type: "Transfer",
      source: "Manual",
      stockCode: "STK002",
      stockDescription: "Stainless Steel Water Bottle",
      location: "Warehouse A → Warehouse C",
      quantity: 40,
      unitPrice: 20.0,
      lineTotal: 800.0,
      userName: "Michael Brown",
      notes: "Transfer to Warehouse C for new regional hub",
    },
    // Customer Returns
    {
      id: "MOV-RET-008",
      reference: "RET-2024-008",
      date: new Date("2024-02-25"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A",
      quantity: 2,
      unitPrice: 25.0,
      lineTotal: 50.0,
      userName: "Emma Wilson",
      notes: "Customer return - changed mind within 30 days",
    },
    {
      id: "MOV-RET-004",
      reference: "RET-2024-004",
      date: new Date("2024-03-28"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A",
      quantity: 3,
      unitPrice: 25.0,
      lineTotal: 75.0,
      userName: "David Lee",
      notes: "Customer return - defective units replaced",
    },
    // Supplier Return
    {
      id: "MOV-SRET-003",
      reference: "SRET-2024-003",
      date: new Date("2024-03-10"),
      type: "Manual Adjustment",
      source: "Manual",
      stockCode: "STK001",
      stockDescription: "Premium Wireless Headphones",
      location: "Warehouse A",
      quantity: -10,
      unitPrice: 25.0,
      lineTotal: -250.0,
      userName: "John Smith",
      notes: "Return to supplier - quality issues identified",
    },
  ];

  // Add specific movements to the beginning of the array
  return [...specificMovements, ...movements];
};

const mockMovements = generateMockMovements();

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

// Format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

// Get badge variant for movement type
const getTypeBadgeVariant = (type: StockMovement["type"]) => {
  switch (type) {
    case "Sales":
      return { variant: "default" as const, accent: "cerulean" as const };
    case "Purchase":
      return { variant: "success" as const, accent: "success" as const };
    case "Opening Balance":
      return { variant: "default" as const, accent: "purple" as const };
    case "Write-off":
      return { variant: "destructive" as const, accent: "destructive" as const };
    case "Transfer":
      return { variant: "warning" as const, accent: "warning" as const };
    case "Manual Adjustment":
      return { variant: "outline" as const, accent: undefined };
    case "Stock Take Adjustment":
      return { variant: "default" as const, accent: "teal" as const };
    default:
      return { variant: "outline" as const, accent: undefined };
  }
};

// Get badge variant for source
const getSourceBadgeVariant = (source: StockMovement["source"]) => {
  switch (source) {
    case "Manual":
      return { variant: "outline" as const, accent: undefined };
    case "ERP Sync":
      return { variant: "default" as const, accent: "cerulean" as const };
    case "System Generated":
      return { variant: "default" as const, accent: "teal" as const };
    default:
      return { variant: "outline" as const, accent: undefined };
  }
};

export function StockMovementsPage() {
  const { navigateTo, pageParams } = useNavigation();

  // Help system
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-movements");

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Advanced Filters state
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [activeFilterRows, setActiveFilterRows] = useState<FilterRow[]>([]);
  const [activeFilterLogic, setActiveFilterLogic] = useState<FilterLogic[]>([]);
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([]);

  // Configure Columns state
  const [isConfigureColumnsModalOpen, setIsConfigureColumnsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);

  // Loading states
  const [isExporting, setIsExporting] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(null);

  // Add Movement Dialog state
  const [isAddMovementDialogOpen, setIsAddMovementDialogOpen] = useState(false);

  // Handle navigation from transaction history
  useEffect(() => {
    if (pageParams?.transactionId) {
      const reference = pageParams.transactionId as string;
      const movement = mockMovements.find((m) => m.reference === reference);
      if (movement) {
        setSelectedMovementId(movement.id);
        setIsDrawerOpen(true);
      }
    }
  }, [pageParams]);

  // Mode state (live/demo toggle)
  const { setMode, isDemo, isLive } = useScreenMode("stock-movements");
  const isLiveMode = isLive;

  // Backend data hook
  const {
    movements: backendMovements,
    pageInfo: backendPageInfo,
    isLoading: isLoadingMovements,
    fetchMovements,
  } = useStockMovements();

  // Cursor-based pagination state for live mode
  const [cursors, setCursors] = useState<string[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
      setCurrentPage(1);
      setCursors([]);
      setCurrentCursorIndex(0);
    },
    [setMode],
  );

  // Fetch data when in live mode
  useEffect(() => {
    if (isLiveMode) {
      // Build sort input based on current sort state (using field/order structure)
      let sortInput: StockMovementSortInput | undefined;
      const order = SortOrder[sortDirection];

      if (sortColumn === "date") {
        sortInput = { field: "MovementDate", order };
      } else if (sortColumn === "reference") {
        sortInput = { field: "MovementReference", order };
      } else if (sortColumn === "type") {
        sortInput = { field: "MovementType", order };
      } else if (sortColumn === "source") {
        sortInput = { field: "MovementSource", order };
      } else if (sortColumn === "stockCode") {
        sortInput = { field: "StockCode", order };
      } else if (sortColumn === "quantity") {
        sortInput = { field: "Quantity", order };
      } else if (sortColumn === "unitPrice") {
        sortInput = { field: "UnitPrice", order };
      } else if (sortColumn === "lineTotal") {
        sortInput = { field: "LineTotal", order };
      }

      // Build filter input
      const filterInput: StockMovementFilterInput = {};
      if (debouncedSearchQuery.trim()) {
        // Search across multiple fields
        filterInput.movementReference = { contains: debouncedSearchQuery };
      }

      // Get the cursor for the current page
      const after = currentCursorIndex > 0 ? cursors[currentCursorIndex - 1] : undefined;

      fetchMovements({
        first: itemsPerPage,
        after,
        filter: Object.keys(filterInput).length > 0 ? filterInput : undefined,
        sort: sortInput,
      }).then(({ pageInfo }) => {
        // Store the end cursor for next page navigation
        if (pageInfo?.endCursor && currentCursorIndex === cursors.length) {
          setCursors((prev) => [...prev, pageInfo.endCursor!]);
        }
      });
    }
  }, [
    isLiveMode,
    debouncedSearchQuery,
    sortColumn,
    sortDirection,
    itemsPerPage,
    currentCursorIndex,
    cursors,
    fetchMovements,
  ]);

  // Reset pagination when filters change in live mode
  useEffect(() => {
    if (isLiveMode) {
      setCursors([]);
      setCurrentCursorIndex(0);
    }
  }, [isLiveMode, debouncedSearchQuery, sortColumn, sortDirection]);

  // Load column configuration from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem("stockMovementsColumnConfig");
    if (savedConfig) {
      try {
        const config: ColumnConfig = JSON.parse(savedConfig);
        setVisibleColumns(config.visibleColumns);
      } catch (error) {
        console.error("Failed to load column configuration:", error);
      }
    }
  }, []);

  // Debounce search query with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter items based on debounced search query (for demo mode)
  const filteredMovements = useMemo(() => {
    // In live mode, filtering is done on the server
    if (isLiveMode) {
      return backendMovements;
    }

    // Demo mode: filter mock data locally
    // Merge service movements with mock movements
    const serviceMovements = getStockMovements();
    let movements = [...serviceMovements, ...mockMovements];

    // Apply advanced filters first
    if (activeFilterRows.length > 0) {
      movements = applyFilters(movements, activeFilterRows, activeFilterLogic);
    }

    // Then apply search query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      movements = movements.filter((movement) => {
        return (
          movement.reference.toLowerCase().includes(query) ||
          movement.stockCode.toLowerCase().includes(query) ||
          movement.stockDescription.toLowerCase().includes(query)
        );
      });
    }

    return movements;
  }, [isLiveMode, backendMovements, debouncedSearchQuery, activeFilterRows, activeFilterLogic]);

  // Sort filtered items (for demo mode - live mode sorting is done on server)
  const sortedMovements = useMemo(() => {
    // In live mode, sorting is done on the server
    if (isLiveMode) {
      return filteredMovements;
    }

    // Demo mode: sort locally
    const movements = [...filteredMovements];

    movements.sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortColumn) {
        case "date":
          aValue = a.date.getTime();
          bValue = b.date.getTime();
          break;
        case "reference":
          aValue = a.reference;
          bValue = b.reference;
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "source":
          aValue = a.source;
          bValue = b.source;
          break;
        case "stockCode":
          aValue = a.stockCode;
          bValue = b.stockCode;
          break;
        case "quantity":
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case "unitPrice":
          aValue = a.unitPrice;
          bValue = b.unitPrice;
          break;
        case "lineTotal":
          aValue = a.lineTotal;
          bValue = b.lineTotal;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return movements;
  }, [isLiveMode, filteredMovements, sortColumn, sortDirection]);

  // Paginate sorted items
  const paginatedMovements = useMemo(() => {
    // In live mode, data is already paginated from the server
    if (isLiveMode) {
      return sortedMovements;
    }
    // In demo mode, paginate locally
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedMovements.slice(startIndex, endIndex);
  }, [isLiveMode, sortedMovements, currentPage, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(sortedMovements.length / itemsPerPage);
  const startItem = sortedMovements.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, sortedMovements.length);

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column with ascending direction
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle row click
  const handleRowClick = (movement: StockMovement) => {
    setSelectedMovementId(movement.id);
    setIsDrawerOpen(true);
  };

  // Handle load related movement (for transfers)
  const handleLoadRelatedMovement = (movementId: string) => {
    setSelectedMovementId(movementId);
  };

  // Advanced Filters handlers
  const handleApplyFilters = (filterRows: FilterRow[], filterLogic: FilterLogic[]) => {
    setActiveFilterRows(filterRows);
    setActiveFilterLogic(filterLogic);
    setActiveFilterChips(generateFilterChips(filterRows, filterLogic));
    setIsFiltersModalOpen(false);
    toast.success(`${filterRows.length} filter(s) applied successfully`);
  };

  const handleRemoveFilterChip = (index: number) => {
    const updatedFilterRows = activeFilterRows.filter((_, i) => i !== index);
    const updatedFilterLogic = activeFilterLogic.filter((_, i) => i !== index);

    setActiveFilterRows(updatedFilterRows);
    setActiveFilterLogic(updatedFilterLogic);
    setActiveFilterChips(generateFilterChips(updatedFilterRows, updatedFilterLogic));

    if (updatedFilterRows.length === 0) {
      toast.success("All filters cleared");
    } else {
      toast.success("Filter removed");
    }
  };

  const handleClearAllFilters = () => {
    setActiveFilterRows([]);
    setActiveFilterLogic([]);
    setActiveFilterChips([]);
    toast.success("All filters cleared");
  };

  // Column configuration handlers
  const handleSaveColumnConfig = (config: ColumnConfig) => {
    setVisibleColumns(config.visibleColumns);
    localStorage.setItem("stockMovementsColumnConfig", JSON.stringify(config));
    toast.success("Column configuration saved");
  };

  // Check if column is visible
  const isColumnVisible = (columnId: string) => {
    return visibleColumns.includes(columnId);
  };

  // Export handlers
  const handleExportCurrentViewCSV = async () => {
    if (sortedMovements.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, sortedMovements.length > 100 ? 1000 : 500));

      const visibleCols = getVisibleColumns(visibleColumns, ALL_COLUMNS);
      const preparedData = prepareDataForExport(sortedMovements, visibleCols);
      const filename = `stock-movements-current-view-${getCurrentDateForFilename()}.csv`;

      exportToCSV(
        preparedData,
        visibleCols.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${sortedMovements.length} movements exported to CSV`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCurrentViewExcel = async () => {
    if (sortedMovements.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, sortedMovements.length > 100 ? 1000 : 500));

      const visibleCols = getVisibleColumns(visibleColumns, ALL_COLUMNS);
      const preparedData = prepareDataForExport(sortedMovements, visibleCols);
      const filename = `stock-movements-current-view-${getCurrentDateForFilename()}.xlsx`;

      exportToExcel(
        preparedData,
        visibleCols.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${sortedMovements.length} movements exported to Excel`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllDataCSV = async () => {
    // Get all movements (service + mock)
    const serviceMovements = getStockMovements();
    const allMovements = [...serviceMovements, ...mockMovements];

    if (allMovements.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      if (allMovements.length > 1000) {
        toast.info("Large export in progress...");
      }

      await new Promise((resolve) => setTimeout(resolve, allMovements.length > 1000 ? 2000 : 500));

      const allCols = getAllColumns(ALL_COLUMNS);
      const preparedData = prepareDataForExport(allMovements, allCols);
      const filename = `stock-movements-all-data-${getCurrentDateForFilename()}.csv`;

      exportToCSV(
        preparedData,
        allCols.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${allMovements.length} movements exported to CSV`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllDataExcel = async () => {
    // Get all movements (service + mock)
    const serviceMovements = getStockMovements();
    const allMovements = [...serviceMovements, ...mockMovements];

    if (allMovements.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      if (allMovements.length > 1000) {
        toast.info("Large export in progress...");
      }

      await new Promise((resolve) => setTimeout(resolve, allMovements.length > 1000 ? 2000 : 500));

      const allCols = getAllColumns(ALL_COLUMNS);
      const preparedData = prepareDataForExport(allMovements, allCols);
      const filename = `stock-movements-all-data-${getCurrentDateForFilename()}.xlsx`;

      exportToExcel(
        preparedData,
        allCols.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${allMovements.length} movements exported to Excel`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Stock Movements</h1>
            {isDemo ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Mode
              </Badge>
            )}
            {isLoadingMovements && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-muted-foreground mt-2">
            View and track all stock movement transactions across your inventory
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <TestTube2 className={`h-4 w-4 ${isDemo ? "text-yellow-600" : "text-muted-foreground"}`} />
            <Switch checked={isLive} onCheckedChange={handleModeToggle} aria-label="Toggle live mode" />
            <Monitor className={`h-4 w-4 ${isLive ? "text-green-600" : "text-muted-foreground"}`} />
          </div>
          <HelpButton onClick={toggleHelp} showLabel />
          <Button className="flex items-center gap-2" onClick={() => setIsAddMovementDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Movement
          </Button>
        </div>
      </div>

      {/* Action Toolbar with Search */}
      <div className="flex items-center gap-4 w-full">
        <div className="relative w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by reference, stock code, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant={activeFilterChips.length > 0 ? "default" : "outline"}
          onClick={() => setIsFiltersModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Advanced Filters
          {activeFilterChips.length > 0 && (
            <Badge variant="secondary" className="ml-1 bg-white text-primary">
              {activeFilterChips.length}
            </Badge>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsConfigureColumnsModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Settings2 className="h-4 w-4" />
          Configure Columns
        </Button>

        {/* Export Button with Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              disabled={isExporting}
              aria-label="Export options"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleExportCurrentViewCSV} className="cursor-pointer">
              Export Current View to CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportCurrentViewExcel} className="cursor-pointer">
              Export Current View to Excel
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportAllDataCSV} className="cursor-pointer">
              Export All Data to CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportAllDataExcel} className="cursor-pointer">
              Export All Data to Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Active Filter Chips */}
      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterChips.map((chip, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 pl-3 pr-2 py-1.5 text-sm font-normal"
            >
              {chip.displayText}
              <button
                onClick={() => handleRemoveFilterChip(index)}
                className="ml-2 hover:text-teal-900 dark:hover:text-teal-100 transition-colors"
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <button
            onClick={handleClearAllFilters}
            className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Result Count */}
      {debouncedSearchQuery || activeFilterChips.length > 0 ? (
        <div className="text-sm text-muted-foreground">
          Showing {sortedMovements.length} of {getStockMovements().length + mockMovements.length} movements
        </div>
      ) : null}

      {/* Table or Empty State */}
      {sortedMovements.length > 0 ? (
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  {isColumnVisible("date") && (
                    <TableHead
                      className="font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("date")}
                    >
                      Date {renderSortIcon("date")}
                    </TableHead>
                  )}
                  {isColumnVisible("reference") && (
                    <TableHead
                      className="font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("reference")}
                    >
                      Reference {renderSortIcon("reference")}
                    </TableHead>
                  )}
                  {isColumnVisible("type") && (
                    <TableHead
                      className="font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("type")}
                    >
                      Type {renderSortIcon("type")}
                    </TableHead>
                  )}
                  {isColumnVisible("source") && (
                    <TableHead
                      className="font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("source")}
                    >
                      Source {renderSortIcon("source")}
                    </TableHead>
                  )}
                  {isColumnVisible("stockCode") && (
                    <TableHead
                      className="font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("stockCode")}
                    >
                      Stock Code {renderSortIcon("stockCode")}
                    </TableHead>
                  )}
                  {isColumnVisible("description") && (
                    <TableHead className="font-semibold min-w-[250px]">Description</TableHead>
                  )}
                  {isColumnVisible("location") && <TableHead className="font-semibold">Location</TableHead>}
                  {isColumnVisible("quantity") && (
                    <TableHead
                      className="font-semibold text-right cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("quantity")}
                    >
                      Quantity {renderSortIcon("quantity")}
                    </TableHead>
                  )}
                  {isColumnVisible("unitPrice") && (
                    <TableHead
                      className="font-semibold text-right cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("unitPrice")}
                    >
                      Unit Price {renderSortIcon("unitPrice")}
                    </TableHead>
                  )}
                  {isColumnVisible("totalValue") && (
                    <TableHead
                      className="font-semibold text-right cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort("lineTotal")}
                    >
                      Total Value {renderSortIcon("lineTotal")}
                    </TableHead>
                  )}
                  {isColumnVisible("user") && <TableHead className="font-semibold">User</TableHead>}
                  {isColumnVisible("notes") && <TableHead className="font-semibold min-w-[200px]">Notes</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMovements.map((movement, index) => (
                  <TableRow
                    key={movement.id}
                    className={`${
                      index % 2 === 0 ? "bg-muted/30" : ""
                    } cursor-pointer hover:bg-muted/50 transition-colors`}
                    onClick={() => handleRowClick(movement)}
                  >
                    {isColumnVisible("date") && (
                      <TableCell className="font-mono text-sm">{formatDate(movement.date)}</TableCell>
                    )}
                    {isColumnVisible("reference") && (
                      <TableCell className="font-medium">{movement.reference}</TableCell>
                    )}
                    {isColumnVisible("type") && (
                      <TableCell>
                        <Badge {...getTypeBadgeVariant(movement.type)}>{movement.type}</Badge>
                      </TableCell>
                    )}
                    {isColumnVisible("source") && (
                      <TableCell>
                        <Badge {...getSourceBadgeVariant(movement.source)}>{movement.source}</Badge>
                      </TableCell>
                    )}
                    {isColumnVisible("stockCode") && <TableCell className="font-mono">{movement.stockCode}</TableCell>}
                    {isColumnVisible("description") && (
                      <TableCell className="max-w-[300px] truncate">{movement.stockDescription}</TableCell>
                    )}
                    {isColumnVisible("location") && <TableCell>{movement.location || "-"}</TableCell>}
                    {isColumnVisible("quantity") && (
                      <TableCell className="text-right font-mono">
                        <span
                          className={
                            movement.quantity > 0
                              ? "text-green-600 dark:text-green-400"
                              : movement.quantity < 0
                                ? "text-red-600 dark:text-red-400"
                                : "text-muted-foreground"
                          }
                        >
                          {movement.quantity > 0 ? "+" : ""}
                          {movement.quantity.toLocaleString()}
                        </span>
                      </TableCell>
                    )}
                    {isColumnVisible("unitPrice") && (
                      <TableCell className="text-right font-mono">{formatCurrency(movement.unitPrice)}</TableCell>
                    )}
                    {isColumnVisible("totalValue") && (
                      <TableCell className="text-right font-mono font-semibold">
                        <span className={movement.lineTotal < 0 ? "text-red-600 dark:text-red-400" : ""}>
                          {formatCurrency(movement.lineTotal)}
                        </span>
                      </TableCell>
                    )}
                    {isColumnVisible("user") && <TableCell>{movement.userName}</TableCell>}
                    {isColumnVisible("notes") && (
                      <TableCell className="max-w-[250px] truncate text-muted-foreground text-sm">
                        {movement.notes || "-"}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-4">
                {isLiveMode ? (
                  <>
                    Showing {paginatedMovements.length} movements (Page {currentCursorIndex + 1})
                  </>
                ) : (
                  <>
                    Showing {startItem}-{endItem} of {sortedMovements.length} movements
                  </>
                )}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {isLiveMode ? (
                <>
                  {/* Previous Page Button (cursor-based) */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentCursorIndex(Math.max(0, currentCursorIndex - 1))}
                    disabled={currentCursorIndex === 0 || isLoadingMovements}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* Page indicator */}
                  <span className="text-sm text-muted-foreground px-2">Page {currentCursorIndex + 1}</span>

                  {/* Next Page Button (cursor-based) */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentCursorIndex(currentCursorIndex + 1)}
                    disabled={!backendPageInfo?.hasNextPage || isLoadingMovements}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  {/* First Page Button */}
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>

                  {/* Previous Page Button */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {/* Page Number Buttons */}
                  {getPageNumbers().map((page, index) => (
                    <Button
                      key={index}
                      variant={page === currentPage ? "default" : "outline"}
                      size="icon"
                      onClick={() => typeof page === "number" && setCurrentPage(page)}
                      disabled={typeof page === "string"}
                      className={typeof page === "string" ? "cursor-default" : ""}
                    >
                      {page}
                    </Button>
                  ))}

                  {/* Next Page Button */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                  {/* Last Page Button */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-full bg-muted p-3">
              <ArrowsUpFromLine className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-1">No stock movements yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Stock movements will appear here once you create them or sync from your ERP
              </p>
              <Button onClick={() => setIsAddMovementDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Movement
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={isFiltersModalOpen}
        onOpenChange={setIsFiltersModalOpen}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilterRows}
        initialLogic={activeFilterLogic}
        entityType="stockMovements"
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={isConfigureColumnsModalOpen}
        onOpenChange={setIsConfigureColumnsModalOpen}
        columns={ALL_COLUMNS}
        currentConfig={{ visibleColumns, columnOrder: visibleColumns }}
        defaultConfig={{ visibleColumns: DEFAULT_VISIBLE_COLUMNS, columnOrder: DEFAULT_VISIBLE_COLUMNS }}
        onSave={handleSaveColumnConfig}
        minVisibleColumns={4}
      />

      {/* Stock Movement Details Drawer */}
      <StockMovementDetailsDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        movementId={selectedMovementId}
        onLoadRelatedMovement={handleLoadRelatedMovement}
      />

      <ContextualHelpDrawer open={isHelpOpen} onOpenChange={setIsHelpOpen} helpContent={helpContent} />

      {/* Add Movement Dialog */}
      <Dialog open={isAddMovementDialogOpen} onOpenChange={setIsAddMovementDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Stock Movement</DialogTitle>
            <DialogDescription>
              Select the type of stock movement you want to create
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-4">
            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Sales" });
              }}
            >
              <div className="rounded-full bg-blue-100 p-2">
                <ArrowDown className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Sales</div>
                <div className="text-sm text-muted-foreground">Record stock sold to customers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-green-50 hover:border-green-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Purchase" });
              }}
            >
              <div className="rounded-full bg-green-100 p-2">
                <ArrowUp className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Purchase</div>
                <div className="text-sm text-muted-foreground">Record stock received from suppliers</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Opening Balance" });
              }}
            >
              <div className="rounded-full bg-purple-100 p-2">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Opening Balance</div>
                <div className="text-sm text-muted-foreground">Set initial stock quantities</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-red-50 hover:border-red-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Write-off" });
              }}
            >
              <div className="rounded-full bg-red-100 p-2">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Write-off</div>
                <div className="text-sm text-muted-foreground">Remove damaged or obsolete stock</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-orange-50 hover:border-orange-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("stock-transfers/create/step1");
              }}
            >
              <div className="rounded-full bg-orange-100 p-2">
                <ArrowRightLeft className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Stock Transfer</div>
                <div className="text-sm text-muted-foreground">Move stock between locations</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-auto py-4 px-4 flex items-start gap-3 hover:bg-gray-50 hover:border-gray-300"
              onClick={() => {
                setIsAddMovementDialogOpen(false);
                navigateTo("add-movement", { type: "Manual Adjustment" });
              }}
            >
              <div className="rounded-full bg-gray-100 p-2">
                <Edit className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Manual Adjustment</div>
                <div className="text-sm text-muted-foreground">Correct stock quantities</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
